import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { UserPlus, CheckCircle, Loader2 } from "lucide-react";

export default function AcceptInvite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const [isProcessing, setIsProcessing] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [isAccepted, setIsAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasTimeout, setHasTimeout] = useState(false);

  // Timeout mechanism
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isProcessing && !isLoggedIn && !error) {
        console.log("⏱️ Timeout: Processing took too long");
        setHasTimeout(true);
        setIsProcessing(false);
        setError("The invitation link processing timed out. The link may have expired or already been used.");
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeoutId);
  }, [isProcessing, isLoggedIn, error]);

  useEffect(() => {
    const handleInviteFlow = async () => {
      console.log("🔍 AcceptInvite: Starting invite flow");
      console.log("🔍 URL:", window.location.href);
      
      const queryParams = new URLSearchParams(window.location.search);
      const customToken = queryParams.get('token');

      // NEW: Handle custom invitation token (bypasses auth-bridge)
      if (customToken) {
        console.log("🎟️ Processing custom invitation token");
        setIsProcessing(true);
        
        try {
          // Verify token via edge function
          const { data: verifyData, error: verifyError } = await supabase.functions.invoke(
            'verify-invitation-token',
            {
              body: { token: customToken }
            }
          );

          if (verifyError || !verifyData?.session) {
            console.error('Token verification failed:', verifyError, verifyData);
            const errorMsg = verifyData?.error || verifyError?.message || 'Invalid or expired invitation link';
            setError(errorMsg);
            setIsProcessing(false);
            
            // Show specific error message based on the error
            let description = 'This invitation link is invalid or has expired. Please request a new invitation.';
            if (errorMsg.includes('expired')) {
              description = 'This invitation link has expired. Please request a new invitation from your administrator.';
            } else if (errorMsg.includes('used')) {
              description = 'This invitation link has already been used. If you need access, please request a new invitation.';
            }
            
            toast({
              title: 'Invalid Invitation',
              description,
              variant: 'destructive',
            });
            return;
          }

          // Set session from returned tokens
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: verifyData.session.access_token,
            refresh_token: verifyData.session.refresh_token,
          });

          if (sessionError) {
            console.error('Failed to set session:', sessionError);
            setError('Failed to authenticate');
            setIsProcessing(false);
            return;
          }

          // Wait for session to be persisted (single check with delay)
          console.log('⏳ Waiting for session to persist...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          const { data: { session } } = await supabase.auth.getSession();
          
          if (!session?.access_token || session.access_token !== verifyData.session.access_token) {
            console.error('❌ Session failed to persist');
            setError('Failed to establish session. Please try again.');
            setIsProcessing(false);
            return;
          }
          
          console.log('✅ Session persisted successfully');

          // Get tenant data including slug
          const { data: tenantData } = await supabase
            .from('tenants')
            .select('name, slug')
            .eq('id', verifyData.tenant_id)
            .single();

          setInviteEmail(verifyData.email);
          setTenantName(tenantData?.name || '');
          
          // Store tenant information for TenantContext
          if (tenantData) {
            localStorage.setItem('selectedTenantId', verifyData.tenant_id);
            localStorage.setItem('selectedTenantSlug', tenantData.slug);
          }
          
          toast({
            title: 'Invitation Accepted',
            description: 'Redirecting to set up your password...',
          });

          // Redirect to password setup with tenant slug (not ID)
          setTimeout(() => {
            navigate(`/setup-password?tenant=${tenantData?.slug || ''}`);
          }, 1000);
          
        } catch (error: any) {
          console.error('Error processing custom token:', error);
          setError(error.message || 'Failed to process invitation');
          setIsProcessing(false);
        }
        return;
      }

      // OLD FLOW: Handle legacy Supabase auth links (for backward compatibility)
      console.log("🔍 Hash:", window.location.hash);
      console.log("🔍 Search:", window.location.search);
      const hashParams = new URLSearchParams(
        window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash
      );

      const getParam = (key: string) => queryParams.get(key) ?? hashParams.get(key);

      const accessToken = getParam('access_token');
      const refreshToken = getParam('refresh_token');
      const tokenHash = getParam('token_hash');
      const type = getParam('type');
      const code = getParam('code');
      const tenantIdParam = getParam('tenant_id');

      // Check if user is already logged in
      const { data: { session: existingSession } } = await supabase.auth.getSession();

      if (existingSession?.user) {
        // User is already logged in
        setIsLoggedIn(true);
        setCurrentUserEmail(existingSession.user.email || "");
        
        // Try to extract tenant info from the invite link metadata
        if (tenantIdParam) {
          const { data: tenantData } = await supabase
            .from('tenants')
            .select('name')
            .eq('id', tenantIdParam)
            .single();
          
          if (tenantData) {
            setTenantName(tenantData.name);
          }
        }
        
        setIsProcessing(false);
        return;
      }

      // User is not logged in, process the recovery/invite link normally
      if (code) {
        const { data, error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) {
          console.error('Failed to exchange code:', error);
          toast({
            title: 'Invalid or Expired Link',
            description: `The invitation link is invalid or has expired. ${error.message || 'Please contact your administrator.'}`,
            variant: 'destructive',
          });
          setIsProcessing(false);
        } else {
          setInviteEmail(data.user?.email || "");
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Check if password setup is required
          const { data: userData } = await supabase
            .from('app_users')
            .select('require_password_reset')
            .eq('id', data.user.id)
            .single();
          
          // Get tenant info for the redirect message
          const { data: membershipData } = await supabase
            .from('memberships')
            .select('tenant:tenants(name)')
            .eq('user_id', data.user.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();
          
          const tenantName = membershipData?.tenant?.name || 'your organization';
          
          setTimeout(() => {
            if (userData?.require_password_reset) {
              // New user - needs to set password
              navigate('/setup-password');
            } else {
              // Existing user - sign out and redirect to login with warning
              supabase.auth.signOut().then(() => {
                navigate(`/auth?returning_user=true&tenant_name=${encodeURIComponent(tenantName)}&email=${encodeURIComponent(data.user.email || '')}`);
              });
            }
          }, 2000);
        }
      } else if (accessToken && refreshToken) {
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (error) {
          console.error('Failed to set session:', error);
          toast({
            title: 'Authentication Error',
            description: `Failed to authenticate. ${error.message || 'Please try clicking the link again.'}`,
            variant: 'destructive',
          });
          setIsProcessing(false);
        } else {
          setInviteEmail(data.user?.email || "");
          window.history.replaceState({}, document.title, window.location.pathname);
          
          const { data: userData } = await supabase
            .from('app_users')
            .select('require_password_reset')
            .eq('id', data.user.id)
            .single();
          
          setTimeout(() => {
            if (userData?.require_password_reset) {
              navigate('/setup-password');
            } else {
              navigate('/dashboard');
            }
          }, 2000);
        }
      } else if (tokenHash && type) {
        const { data, error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: type as any,
        });
        if (error) {
          console.error('Failed to verify token:', error);
          toast({
            title: 'Invalid or Expired Link',
            description: `The invitation link is invalid or has expired. ${error.message || 'Please contact your administrator.'}`,
            variant: 'destructive',
          });
          setIsProcessing(false);
        } else {
          setInviteEmail(data.user?.email || "");
          window.history.replaceState({}, document.title, window.location.pathname);
          
          const { data: userData } = await supabase
            .from('app_users')
            .select('require_password_reset')
            .eq('id', data.user.id)
            .single();
          
          setTimeout(() => {
            if (userData?.require_password_reset) {
              navigate('/setup-password');
            } else {
              navigate('/dashboard');
            }
          }, 2000);
        }
      } else {
        console.log("⚠️ No auth parameters found in URL");
        setError("This invitation link appears to be invalid or missing required parameters.");
        toast({
          title: 'Invalid Link',
          description: 'This invitation link appears to be invalid or missing required parameters.',
          variant: 'destructive',
        });
        setIsProcessing(false);
      }
    };

    handleInviteFlow();
  }, [searchParams, toast, navigate]);

  const handleAcceptInvite = async () => {
    setIsProcessing(true);
    
    try {
      // The membership should already be created by the invite-user function
      // We just need to refresh the tenant context and redirect
      toast({
        title: 'Invitation Accepted',
        description: `You now have access to ${tenantName || 'this organization'}.`,
      });
      
      setIsAccepted(true);
      
      // Redirect to dashboard after a brief delay
      setTimeout(() => {
        navigate('/dashboard');
        window.location.reload(); // Reload to refresh tenant context
      }, 2000);
    } catch (error: any) {
      console.error('Error accepting invite:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to accept invitation. Please try again.',
        variant: 'destructive',
      });
      setIsProcessing(false);
    }
  };

  const handleLogoutAndAccept = () => {
    // Log out and reload the page to process the invite link
    supabase.auth.signOut().then(() => {
      window.location.reload();
    });
  };

  // Show error state
  if (error || hasTimeout) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <main className="w-full max-w-md">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl text-destructive">Invitation Link Issue</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {error || "The invitation link processing timed out. The link may have expired or already been used."}
              </p>
              <div className="space-y-2 p-4 bg-muted rounded-lg">
                <p className="text-sm font-medium">What to do next:</p>
                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                  <li>Request a new invitation from your administrator</li>
                  <li>Make sure you're using the latest invitation link</li>
                  <li>If you were already logged in, try logging out first</li>
                  <li>Check that you clicked the link from the most recent email</li>
                </ul>
              </div>
              <Button onClick={() => navigate("/auth")} className="w-full">
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Show logged-in user flow
  if (isLoggedIn && !isAccepted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <main className="w-full max-w-md">
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <UserPlus className="h-6 w-6 text-primary" />
              </div>
              <CardTitle className="text-2xl">Accept Invitation</CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                You're currently logged in as <span className="font-medium">{currentUserEmail}</span>
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">
                  You've been invited to join:
                </p>
                <p className="font-medium">
                  {tenantName || "a new organization"}
                </p>
              </div>
              
              <div className="space-y-2">
                <Button 
                  onClick={handleAcceptInvite} 
                  className="w-full"
                  disabled={isProcessing}
                >
                  {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Accept & Continue as {currentUserEmail}
                </Button>
                
                <Button 
                  onClick={handleLogoutAndAccept}
                  variant="outline"
                  className="w-full"
                  disabled={isProcessing}
                >
                  Log out & Accept with Different Account
                </Button>
              </div>

              <p className="text-xs text-center text-muted-foreground">
                Click "Accept" to join this organization with your current account
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Show success state
  if (isAccepted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <main className="w-full max-w-md">
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-6 w-6 text-primary" />
              </div>
              <CardTitle className="text-2xl">Invitation Accepted!</CardTitle>
              <p className="text-sm text-muted-foreground">
                Redirecting to your dashboard...
              </p>
            </CardHeader>
            <CardContent className="text-center">
              <div className="flex justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Show processing/authenticating state
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <main className="w-full max-w-md">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <UserPlus className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">
              {inviteEmail ? "Welcome to Lysara!" : "Processing Invitation..."}
            </CardTitle>
            {inviteEmail && (
              <p className="text-sm text-muted-foreground">
                Your invitation for {inviteEmail} is being processed.
              </p>
            )}
          </CardHeader>
          <CardContent className="text-center">
            <div className="flex justify-center mb-4">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
            <p className="text-sm text-muted-foreground">
              {inviteEmail ? "Redirecting..." : "Please wait while we verify your invitation..."}
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
